import { c as _c } from "react/compiler-runtime";
import './commitment-avatar.scss';
import type { StaticImageData } from 'next/image';
import type { PropsWithChildren } from 'react';
import React, { useCallback } from 'react';
import { ChildImage } from '@sola/ark-core/atoms/child-image/child-image.tsx';
import { Image } from '@sola/ark-core/atoms/image/image.tsx';
import { ChildImageVariantEnum } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import type { Either } from '@sola/schema/dotcom/helpers.ts';
import type { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.ts';
import { GiftFundCodesEnum } from '@sola/schema/dotcom/shared/gifts.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';

// NOTE Should this be a setting in SDG?
export const defaultIcon: IconNames = 'heart-hand';
const sizeMap = {
  sm: 36,
  lg: 48,
  xl: 88
};
type AvatarCommitment = Pick<GraphQLCommitment, 'participant'> & {
  commitmentType?: GraphQLCommitment['commitmentType'];
  fund?: Partial<GraphQLCommitment['fund']>;
};
export type WithoutText = {
  imgSrc?: string | StaticImageData;
  commitment?: AvatarCommitment;
};
export type WithText = {
  text: string;
};
type CommitmentAvatarWrapperProps = {
  className?: string;
  purpose?: DisplayPurposeProps['variants'];
  responsive?: boolean;
  size?: 'sm' | 'lg' | 'xl';
  isStacked?: boolean;
};
export type CommitmentAvatarProps = Either<WithText, WithoutText> & CommitmentAvatarWrapperProps;
const GiftIconMap: Record<string, IconNames> = {
  [GiftFundCodesEnum.Birthday]: 'birthday-cake',
  [GiftFundCodesEnum.Child]: 'beneficiary',
  [GiftFundCodesEnum.Family]: 'family-gift',
  [GiftFundCodesEnum.Center]: 'church'
} as const;

// @TODO: Refactor this "wrapper" into it's own atom that can be used without "CommitmentAvatarWrapper" for things like TaxReceipt and other uses
export const CommitmentAvatarWrapper: React.FC<PropsWithChildren<CommitmentAvatarWrapperProps>> = t0 => {
  const $ = _c(10);
  const {
    children,
    className,
    purpose,
    responsive,
    size: t1,
    isStacked: t2
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const isStacked = t2 === undefined ? false : t2;
  const purposeClass = purpose ? `CommitmentAvatar--${purpose}` : null;
  const t3 = size === "lg";
  const t4 = size === "xl";
  let t5;
  if ($[0] !== className || $[1] !== isStacked || $[2] !== purposeClass || $[3] !== responsive || $[4] !== t3 || $[5] !== t4) {
    t5 = cn("CommitmentAvatar rounded-full flex items-center justify-center relative", [purposeClass, className, {
      "CommitmentAvatar--responsive md:label-xl": responsive,
      "CommitmentAvatar--large label-xl": t3,
      "CommitmentAvatar--xlarge label-xl": t4,
      "CommitmentAvatar--stacked": isStacked
    }]);
    $[0] = className;
    $[1] = isStacked;
    $[2] = purposeClass;
    $[3] = responsive;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== children || $[8] !== t5) {
    t6 = <div className={t5}>{children}</div>;
    $[7] = children;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
export const CommitmentAvatar: React.FC<CommitmentAvatarProps> = ({
  commitment,
  text,
  imgSrc,
  size = 'sm',
  responsive,
  ...wrapperProps
}) => {
  const Icon = useCallback((): null | React.ReactNode => {
    if (text) {
      return <span className="label-md label-potent">{text}</span>;
    }
    if (imgSrc) {
      return <Image className="absolute w-full h-full" src={imgSrc} alt="" width={48} height={48} />;
    }
    if (commitment?.commitmentType === CommitmentTypeEnum.ChildSponsorship) {
      const imageSize = (!responsive ? sizeMap[size] : sizeMap.lg) * 2;
      return commitment?.participant?.photoUrl ? <ChildImage childImageUrl={commitment?.participant.photoUrl} alt="Participant" variant={ChildImageVariantEnum.Circle} className="CommitmentAvatar__image" width={imageSize} /> : <ArkIcon iconName="beneficiary" />;
    }
    let iconName = commitment?.fund?.icon as IconNames;
    if (commitment?.commitmentType === CommitmentTypeEnum.GiftCommitment) {
      iconName = GiftIconMap[commitment?.fund?.code as GiftFundCodesEnum] ?? iconName;
    }
    return <ArkIcon iconName={iconName || defaultIcon} />;
  }, [commitment?.participant?.globalId || commitment?.fund?.code, text]);
  return <CommitmentAvatarWrapper responsive={responsive} size={size} {...wrapperProps}>
      <Icon />
    </CommitmentAvatarWrapper>;
};